import React from 'react';
import { CustomerRecord } from './DuplicateCard';
import { HeatmapCell } from './HeatmapCell';
type FieldConfig = {
  name: keyof CustomerRecord;
  label: string;
  format?: (value: string) => string;
};
type HeatmapGridProps = {
  records: CustomerRecord[];
  fieldConfigs: FieldConfig[];
  fieldSelections: {
    [fieldName: string]: string | null;
  };
  onCellClick: (fieldName: string, recordId: string) => void;
  confidenceThreshold: number;
};
export function HeatmapGrid({
  records,
  fieldConfigs,
  fieldSelections,
  onCellClick,
  confidenceThreshold
}: HeatmapGridProps) {
  // Calculate quality score for a cell
  const calculateQualityScore = (record: CustomerRecord, fieldName: keyof CustomerRecord, allRecords: CustomerRecord[]): number => {
    const value = String(record[fieldName]);
    const isEmpty = !value || value === 'undefined' || value.trim() === '';
    if (isEmpty) return 0;
    let score = 0;
    // Completeness: 40 points
    score += 40;
    // Recency: 30 points (newer records score higher)
    const recordDate = new Date(record.createdAt).getTime();
    const allDates = allRecords.map(r => new Date(r.createdAt).getTime());
    const maxDate = Math.max(...allDates);
    const minDate = Math.min(...allDates);
    const dateRange = maxDate - minDate;
    if (dateRange > 0) {
      const recencyScore = (recordDate - minDate) / dateRange * 30;
      score += recencyScore;
    } else {
      score += 30; // All same date
    }
    // Frequency: 30 points (most common value scores higher)
    const values = allRecords.map(r => String(r[fieldName]));
    const valueCounts = values.reduce((acc, v) => {
      acc[v] = (acc[v] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const maxCount = Math.max(...Object.values(valueCounts));
    const thisCount = valueCounts[value] || 0;
    const frequencyScore = thisCount / maxCount * 30;
    score += frequencyScore;
    return Math.round(score);
  };
  // Get most frequent value for a field
  const getMostFrequentValue = (fieldName: keyof CustomerRecord): string => {
    const values = records.map(r => String(r[fieldName]));
    const valueCounts = values.reduce((acc, v) => {
      if (v && v !== 'undefined' && v.trim() !== '') {
        acc[v] = (acc[v] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
    const sorted = Object.entries(valueCounts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || '';
  };
  // Get recommended record for a field (highest quality score above threshold)
  const getRecommendedRecordId = (fieldName: keyof CustomerRecord): string | null => {
    const scores = records.map(record => ({
      recordId: record.id,
      score: calculateQualityScore(record, fieldName, records)
    }));
    const best = scores.reduce((max, curr) => curr.score > max.score ? curr : max, scores[0]);
    return best && best.score >= confidenceThreshold ? best.recordId : null;
  };
  // Handle selecting all fields for a record
  const handleSelectAllForRecord = (recordId: string) => {
    fieldConfigs.forEach(config => {
      onCellClick(config.name, recordId);
    });
  };
  const colors = ['bg-blue-50', 'bg-purple-50', 'bg-green-50', 'bg-orange-50', 'bg-pink-50'];
  return <div className="overflow-x-auto" data-id="element-125">
      <div className="inline-block min-w-full" data-id="element-126">
        {/* Header Row - Record Labels */}
        <div className="flex mb-2 pt-1" data-id="element-127">
          <div className="w-40 flex-shrink-0" data-id="element-128"></div>
          {records.map((record, index) => <div key={record.id} className={`w-48 flex-shrink-0 mx-1 px-2 py-2 rounded-lg cursor-pointer transition-all hover:ring-2 hover:ring-blue-400 hover:ring-offset-2 hover:shadow-md relative ${colors[index % colors.length]}`} onClick={() => handleSelectAllForRecord(record.id)} role="button" tabIndex={0} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectAllForRecord(record.id);
          }
        }} aria-label={`Select all fields from Record ${String.fromCharCode(65 + index)}`} data-id="element-129">
              <div className="text-xs font-semibold text-gray-700" data-id="element-130">
                Record {String.fromCharCode(65 + index)}
              </div>
              <div className="text-[10px] text-gray-500 truncate" data-id="element-131">
                {record.id}
              </div>
            </div>)}
        </div>

        {/* Grid Rows */}
        {fieldConfigs.map(config => {
        const mostFrequentValue = getMostFrequentValue(config.name);
        const recommendedRecordId = getRecommendedRecordId(config.name);
        return <div key={config.name} className="flex mb-2 items-center" data-id="element-132">
              {/* Field Label */}
              <div className="w-40 flex-shrink-0 pr-3" data-id="element-133">
                <div className="text-sm font-medium text-gray-700" data-id="element-134">
                  {config.label}
                </div>
              </div>

              {/* Cells */}
              {records.map(record => {
            const value = String(record[config.name]);
            const qualityScore = calculateQualityScore(record, config.name, records);
            const isSelected = fieldSelections[config.name] === record.id;
            const isRecommended = recommendedRecordId === record.id;
            const isMostFrequent = value === mostFrequentValue && value !== '';
            return <div key={record.id} className="w-48 flex-shrink-0 mx-1" data-id="element-135">
                    <HeatmapCell value={value} qualityScore={qualityScore} isSelected={isSelected} isRecommended={isRecommended} isMostFrequent={isMostFrequent} onClick={() => onCellClick(config.name, record.id)} formatValue={config.format} recordLabel={`Record ${String.fromCharCode(65 + records.indexOf(record))}`} fieldLabel={config.label} data-id="element-136" />
                  </div>;
          })}
            </div>;
      })}
      </div>
    </div>;
}